import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import Seo, { localBusinessLd, breadcrumbLd } from "@/lib/seo";
import api from "@/lib/api";

export default function CataloguePage() {
  const [items, setItems] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [family, setFamily] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const location = useLocation();
  const nav = useNavigate();
  const addToProject = location.state?.addToProject;

  useEffect(() => {
    api.get("/prestations").then(({ data }) => { setItems(data.items); setFamilles(data.familles); });
  }, []);

  const filtered = items.filter(i =>
    (!family || i.famille === family) &&
    (!q || i.nom.toLowerCase().includes(q.toLowerCase()) || i.description_client.toLowerCase().includes(q.toLowerCase()))
  );

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));

  const submit = async () => {
    const chosen = items.filter(i => selected[i.id]).map(i => ({
      nom_client: i.nom, famille: i.famille, unite_probable: i.unite, confiance: "haute", justification: "Choisi manuellement", quantite_estimee: null,
    }));
    if (chosen.length === 0) { toast.error("Sélectionnez au moins une prestation."); return; }
    if (addToProject) {
      const { data: p } = await api.get(`/projects/${addToProject}`);
      const next = [...p.prestations, ...chosen];
      await api.put(`/projects/${addToProject}`, { prestations: next });
      nav(`/estimation/${addToProject}`);
    } else {
      const { data } = await api.post("/projects/new");
      await api.put(`/projects/${data.id}`, { prestations: chosen });
      nav(`/estimation/${data.id}`);
    }
  };

  return (
    <div className="pt-[100px] md:pt-[140px] pb-24">
      <Seo
        title="Catalogue des prestations de travaux intérieurs | RAMA DÉCO"
        description="Sélectionnez vos travaux : peinture, placo, sols, carrelage, électricité, plomberie, salle de bains, cuisine. Composez votre projet et obtenez une estimation."
        path="/catalogue"
        jsonLd={[localBusinessLd(), breadcrumbLd([{ to: "/", label: "Accueil" }, { to: "/catalogue", label: "Catalogue" }])]}
      />
      <div className="wrap">
        <div className="font-mono-caption text-bordeaux mb-4">Catalogue</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight text-stone-900 max-w-3xl">Que souhaitez-vous <span className="italic text-bordeaux">rénover ?</span></h1>

        <div className="mt-10 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="catalogue-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un travail (ex : porte)" className="w-full pl-11 pr-4 py-3 bg-white border border-stone-300 rounded-sm focus:border-bordeaux outline-none" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setFamily("")} className={`px-4 py-2 rounded-full text-sm border whitespace-nowrap ${!family ? "bg-stone-900 text-stone-50 border-stone-900" : "bg-transparent border-stone-300"}`}>Toutes</button>
            {familles.map(f => (
              <button key={f} data-testid={`family-${f}`} onClick={() => setFamily(f)} className={`px-4 py-2 rounded-full text-sm border whitespace-nowrap ${family === f ? "bg-stone-900 text-stone-50 border-stone-900" : "bg-transparent border-stone-300"}`}>{f}</button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(i => (
            <button
              key={i.id}
              data-testid={`presta-${i.id}`}
              onClick={() => toggle(i.id)}
              className={`text-left p-6 border rounded-sm transition ${selected[i.id] ? "bg-bordeaux/5 border-bordeaux" : "bg-white border-stone-300 hover:border-stone-500"}`}
            >
              <div className="font-mono-caption text-stone-500 mb-2">{i.famille} · {i.unite}</div>
              <div className="font-display text-lg text-stone-900">{i.nom}</div>
              <div className="mt-2 text-sm text-stone-600 leading-relaxed">{i.description_client}</div>
              {selected[i.id] && <div className="mt-3 text-xs font-semibold text-bordeaux inline-flex items-center gap-1"><Plus size={14} /> Ajouté au projet</div>}
            </button>
          ))}
        </div>

        <div className="fixed bottom-16 md:bottom-6 left-0 right-0 z-30 flex justify-center pointer-events-none">
          {Object.values(selected).some(Boolean) && (
            <button data-testid="catalogue-submit" onClick={submit} className="btn-primary pointer-events-auto shadow-2xl">
              Continuer avec {Object.values(selected).filter(Boolean).length} prestation{Object.values(selected).filter(Boolean).length > 1 ? "s" : ""} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
